import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch, ApiError } from "./apiClient";
import { useConnectors } from "./connectors";
import type { TestHttpLog } from "@/pages/testing/shared";

/**
 * Test aktivace — admin API `/admin/api/v1/testing/activation/**`. `/start` streamuje
 * průběh přes SSE (viz [[sse.ts]]); `/validate` vrací Bearer token (jen eCobra).
 */

/** Auth server typy, které test aktivace podporuje (viz původní controller). */
const SUPPORTED_AUTH_SERVERS = ["ECOBRA", "CSOB_ECHANNELS"] as const;

export interface AuthServerOption {
  value: string;
  label: string;
}

/** Aktivní podporované auth servery odvozené z konektorů (enabled). */
export function useActivationAuthServers(): { options: AuthServerOption[]; isLoading: boolean } {
  const { data, isLoading } = useConnectors();
  const options = (data ?? [])
    .filter((c) => c.enabled && SUPPORTED_AUTH_SERVERS.includes(c.connectorType as (typeof SUPPORTED_AUTH_SERVERS)[number]))
    .map((c) => ({ value: c.connectorType, label: authServerLabel(c.connectorType) }));
  return { options, isLoading };
}

export function authServerLabel(type: string): string {
  switch (type) {
    case "ECOBRA":
      return "eCobra";
    case "CSOB_ECHANNELS":
      return "CSOB eChannels";
    default:
      return type;
  }
}

export interface PaatProfile {
  id: string;
  profileName: string;
  isDefault: boolean;
  platform: string;
}

export function usePaatProfiles() {
  return useQuery({
    queryKey: ["paat-profiles"],
    queryFn: () => apiFetch<PaatProfile[]>("/admin/api/v1/paat-profiles"),
  });
}

export interface ActivationRequest {
  authServerType: string;
  username: string;
  otp: string;
  password: string;
  activationGuid: string;
  pushIdentifier?: string | null;
  applicationId?: string | null;
  paatProfileId?: string | null;
  deviceType?: number | null;
  /** Volitelné TTL v sekundách; vylučuje se s `expiresAt` (server vrátí 400). */
  expiresInSeconds?: number | null;
  /** Volitelné štítky pro pozdější filtrování a hromadný úklid. */
  tags?: string[] | null;
}

export type ActivationStatus =
  | "STARTED"
  | "VALIDATING"
  | "ACTIVATING"
  | "COLLECTING_LOGS"
  | "COMPLETED"
  | "FAILED";

export interface ActivationResultData {
  tokenId: string;
  username: string;
  activatedAt: string;
  httpLogs: TestHttpLog[];
}

export interface ActivationProgress {
  status: ActivationStatus;
  message: string;
  timestamp: string;
  data?: ActivationResultData;
}

// --- Validate (jen eCobra) ---

export interface ValidateResult {
  success: boolean;
  token?: string | null;
  durationMs?: number;
  testTraceId?: string;
  error?: string;
  message?: string;
  type?: string;
}

export function useValidateActivation() {
  return useMutation({
    mutationFn: async (body: { userName: string; activationOtp: string; activationGuid: string | null }) => {
      try {
        return await apiFetch<ValidateResult>("/admin/api/v1/testing/activation/validate", {
          method: "POST",
          json: body,
        });
      } catch (e) {
        // 400/500/503 nesou tělo `{error,message,type}` — vrať jako neúspěch, ne throw.
        if (e instanceof ApiError && e.body && typeof e.body === "object") {
          return { success: false, ...(e.body as Record<string, unknown>) } as ValidateResult;
        }
        throw e;
      }
    },
  });
}

// --- Generátory (paritní s původním test-activation.js) ---

export function generateGuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function generatePushIdentifier(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
  let suffix = "APA91b";
  for (let i = 0; i < 140; i++) suffix += chars.charAt(Math.floor(Math.random() * chars.length));
  return "efsRJO7W20jih5_-9BrPuM:" + suffix;
}
